import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BEIGE_700, CORAL_100, CORAL_600, GRAY_600, GRAY_700, GRAY_800, GRAY_900, RhetorikLightFontFamily } from 'constants/styling/theme'
import { Divider, IconButton, Link, Stack, Typography } from '@mui/material'
import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react'
import { PropstackChainIcon, PropstackIconTile } from './PropstackIcons'

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseIcon from '@mui/icons-material/Close'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { Path } from 'constants/router'
import { Link as RouterLink } from 'react-router-dom'
import { UserWorkspaceMembership } from 'models/user'
import { useSetPropstackBannerDismissal } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Propstack membership data for this switcher row (looked up by workspace id by the caller) */
  workspace?: UserWorkspaceMembership
  /** Whether the current user is an admin OF THIS ROW'S WORKSPACE (not the active workspace) */
  isAdmin: boolean
  /** Reports open/close so the switcher won't collapse while the (portaled) popover is in use (QA-303). */
  onPopoverToggle?: (open: boolean) => void
}

/**
 * "Propstack-linked" pill for a workspace-switcher row; opens a popover explaining the workspace's
 * origin, with a banner-restore action when dismissed (ISSUE-2136). Self-gating (flag + linked),
 * and stops click propagation so the badge/popover never trigger the row's switch handler.
 * @example <PropstackLinkedBadge workspace={workspaceItem} isAdmin={workspaceItem.role === WorkspaceRole.ADMIN} />
 */
export const PropstackLinkedBadge: FC<Props> = ({ workspace, isAdmin, onPopoverToggle }) => {
  const { t } = useTranslation(['workspace_profile'])
  const propstackIntegration = useFeatureFlag(FeatureFlag.PROPSTACK_INTEGRATION)
  const { currentUserWorkspaceMembership } = useUserData()
  const restoreMutation = useSetPropstackBannerDismissal()
  const { spawnErrorToast } = useSnackbar()
  const [isOpen, setIsOpen] = useState(false)

  // Report open/close to the switcher; cleanup balances the count if we unmount while open.
  useEffect(() => {
    if (!isOpen) return
    onPopoverToggle?.(true)
    return () => onPopoverToggle?.(false)
  }, [isOpen, onPopoverToggle])

  if (!propstackIntegration || !workspace?.propstackLinked) return null

  // WORKSPACE_SETTINGS_INTEGRATIONS always targets the ACTIVE workspace, so the
  // learn-more link only makes sense on the active workspace's row.
  const showLearnMore = isAdmin && workspace.workspaceId === currentUserWorkspaceMembership?.workspaceId

  const handleToggle = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation()
    const willOpen = !isOpen
    setIsOpen(willOpen)
    if (willOpen) logAnalyticsEvent(AnalyticsEvent.PROPSTACK_BADGE_POPOVER_OPENED, { workspaceId: workspace.workspaceId })
  }

  const handleRestore = (e: MouseEvent) => {
    e.stopPropagation()
    restoreMutation.mutate(
      { workspaceId: workspace.workspaceId, dismissed: false },
      {
        onSuccess: () => {
          logAnalyticsEvent(AnalyticsEvent.PROPSTACK_BANNER_RESTORED, { workspaceId: workspace.workspaceId })
          setIsOpen(false)
        },
        onError: () => spawnErrorToast(t('propstack_banner.update_error')),
      }
    )
  }

  return (
    <MUIDropdown
      open={isOpen}
      onClose={() => setIsOpen(false)}
      disablePortal={false}
      // QA-303: the popper default (zIndex 1) renders behind the switcher; 1200 clears the
      // top bar (100) and stays below the modal layer (1300).
      sx={{ zIndex: 1200 }}
      button={() => (
        <span
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            handleToggle(e)
          }}
        >
          <MUIBadge
            color="coral"
            size="sm"
            rounded
            icon={<PropstackChainIcon size={11} color={CORAL_600} />}
            label={t('propstack_linked')}
            textColor={GRAY_800}
            sx={{ backgroundColor: CORAL_100 }}
          />
        </span>
      )}
    >
      <Stack padding="1.8rem" maxWidth="33.6rem" onClick={(e) => e.stopPropagation()}>

        <Stack direction="row" alignItems="center" gap="1rem" marginBottom="1.3rem">

          <PropstackIconTile size={30} />

          <Typography variant="text-sm" fontWeight={600} color={GRAY_900} flex={1}>{t('propstack_banner.popover_title')}</Typography>

          <IconButton aria-label={t('propstack_banner.close')} size="small" sx={{ color: GRAY_600 }} onClick={() => setIsOpen(false)}>
            <CloseIcon sx={{ fontSize: '1.5rem' }} />
          </IconButton>

        </Stack>

        <Typography fontFamily={RhetorikLightFontFamily} fontWeight={300} fontSize="1.5rem" letterSpacing="-.03em" lineHeight={1.4} color={GRAY_900}>
          {t('propstack_banner.title')}
        </Typography>

        <Typography variant="text-xs" color={GRAY_700} lineHeight={1.55} marginTop=".8rem">
          {t('propstack_banner.body_short')}
        </Typography>

        {(showLearnMore || workspace.propstackBannerDismissed) &&
          <Divider sx={{ margin: '1.5rem 0 1.3rem', borderColor: BEIGE_700 }} />
        }

        {showLearnMore &&
          <Link
            component={RouterLink}
            to={Path.WORKSPACE_SETTINGS_INTEGRATIONS}
            variant="text-xs"
            onClick={() => {
              logAnalyticsEvent(AnalyticsEvent.PROPSTACK_BANNER_LEARN_MORE_CLICKED, { workspaceId: workspace.workspaceId, source: 'badge_popover' })
              setIsOpen(false)
            }}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', width: 'fit-content', color: CORAL_600, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('propstack_banner.learn_more')}
            <ArrowForwardRoundedIcon sx={{ fontSize: '1.3rem' }} />
          </Link>
        }

        {workspace.propstackBannerDismissed &&
          <MUIButton
            variant="text"
            type="secondaryNoBorder"
            size="xs"
            aria-label={t('propstack_banner.restore')}
            isLoading={restoreMutation.isPending}
            onClick={handleRestore}
            sx={{ marginTop: showLearnMore ? '1.1rem' : 0, padding: 0, justifyContent: 'flex-start', width: 'fit-content', color: GRAY_700, textDecoration: 'underline', textUnderlineOffset: '2px', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('propstack_banner.restore')}
          </MUIButton>
        }

      </Stack>
    </MUIDropdown>
  )
}
